"use client";

import { useState } from "react";
import { peeqdAvatarUrl } from "@/libs/peeqdAvatars";
import { proxiedInstagramImageUrl } from "@/libs/instagramImageProxy";
import {
  formatProfileStat,
  type PeeqdProfileData,
} from "@/libs/peeqdProfilePreview";
import { INSTAGRAM_LINK_PROPS, instagramProfileUrl } from "@/libs/instagramUrl";

interface PeeqdProfileHeaderProps {
  profile: PeeqdProfileData;
  loading?: boolean;
  isScraping?: boolean;
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <span className="shrink-0">
      <span className="font-semibold text-ink">{value}</span>{" "}
      <span className="text-muted">{label}</span>
    </span>
  );
}

function HeaderSkeleton() {
  return (
    <div className="flex gap-4 border-b border-line p-4 sm:gap-5 sm:p-5">
      <div className="sk h-[72px] w-[72px] shrink-0 rounded-full sm:h-20 sm:w-20" />
      <div className="min-w-0 flex-1 space-y-2.5 pt-1">
        <div className="sk h-5 w-40" />
        <div className="sk h-3.5 w-28" />
        <div className="sk h-4 w-56" />
        <div className="sk h-3.5 w-full max-w-md" />
        <div className="sk h-3.5 w-32" />
      </div>
    </div>
  );
}

function ProfileAvatar({ profile }: { profile: PeeqdProfileData }) {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const proxiedAvatar = proxiedInstagramImageUrl(profile.avatarUrl);
  const showRealAvatar = Boolean(proxiedAvatar) && !avatarFailed;
  const mockAvatar = peeqdAvatarUrl(160, profile.avatarIndex);

  if (showRealAvatar) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={proxiedAvatar!}
        alt=""
        className="h-[72px] w-[72px] shrink-0 rounded-full object-cover sm:h-20 sm:w-20"
        onError={() => setAvatarFailed(true)}
      />
    );
  }

  if (profile.avatarUrl) {
    return <div className="sk h-[72px] w-[72px] shrink-0 rounded-full sm:h-20 sm:w-20" />;
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={mockAvatar}
      alt=""
      className="h-[72px] w-[72px] shrink-0 rounded-full object-cover sm:h-20 sm:w-20"
    />
  );
}

export default function PeeqdProfileHeader({
  profile,
  loading = false,
  isScraping = false,
}: PeeqdProfileHeaderProps) {
  if (loading) return <HeaderSkeleton />;

  const { displayName, bio, mentions, externalUrl } = profile;

  return (
    <div className="border-b border-line p-4 sm:p-5">
      <div className="flex gap-4 sm:gap-5">
        <ProfileAvatar profile={profile} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <a
              href={instagramProfileUrl(profile.username)}
              {...INSTAGRAM_LINK_PROPS}
              className="font-display text-[18px] font-bold leading-tight text-ink hover:text-rose-deep sm:text-[20px]"
            >
              {profile.username}
            </a>
            {profile.verified && (
              <svg className="h-4 w-4 shrink-0 text-sky-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm-1.2 14.3-3.5-3.5 1.4-1.4 2.1 2.1 5.6-5.6 1.4 1.4-7 7Z" />
              </svg>
            )}
            {isScraping && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose/10 px-2 py-0.5 text-[10px] font-semibold text-rose-deep">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose" />
                Updating
              </span>
            )}
          </div>

          <p className="mt-0.5 text-[14px] font-medium text-ink">{displayName}</p>

          <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
            <Stat value={formatProfileStat(profile.postsCount)} label="posts" />
            <Stat value={formatProfileStat(profile.followersCount)} label="followers" />
            <Stat value={formatProfileStat(profile.followingCount)} label="following" />
          </div>
        </div>
      </div>

      {(bio || mentions.length > 0) && (
        <div className="mt-3 text-[13px] leading-relaxed text-ink">
          {bio && <p>{bio}</p>}
          {mentions.length > 0 && (
            <p className="mt-1 text-rose">
              {mentions.map((m) => (
                <a
                  key={m}
                  href={instagramProfileUrl(m)}
                  {...INSTAGRAM_LINK_PROPS}
                  className="mr-2 hover:underline"
                >
                  @{m}
                </a>
              ))}
            </p>
          )}
        </div>
      )}

      {externalUrl && (
        <p className="mt-2 flex items-center gap-1.5 text-[13px] text-rose">
          <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <span>{externalUrl}</span>
        </p>
      )}
    </div>
  );
}
